/**
 * src/context/PlanContext.js
 * Provides plan, usage, and feature-gating helpers to the portal.
 *
 * Fetches plan data from GET /portal/account (which already returns
 * plan + usage info after Step 2 backend changes).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../api/client';

const PlanContext = createContext(null);

export function PlanProvider({ children }) {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPlan = useCallback(async () => {
    if (!user) {
      setPlan(null);
      setUsage(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/portal/account');
      setPlan(res.data.plan || null);
      setUsage(res.data.usage || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load plan info');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const hasFeature = useCallback((featureName) => {
    if (!plan) return true; // fail-open: no plan data = all features available
    return plan.features?.[featureName] === true;
  }, [plan]);

  const getLimit = useCallback((limitName) => {
    if (!plan) return null;
    return plan.limits?.[limitName] ?? null;
  }, [plan]);

  const isOverLimit = useCallback((limitName) => {
    if (!plan || !usage) return false;
    const limit = plan.limits?.[limitName];
    if (limit === null || limit === undefined) return false; // unlimited
    const usageMap = {
      max_events_per_month: usage.events?.current,
      max_processes: usage.processes?.current,
      max_portal_users: usage.portal_users?.current,
    };
    const current = usageMap[limitName];
    if (current === undefined || current === null) return false;
    return current >= limit;
  }, [plan, usage]);

  const usagePercent = useCallback((limitName) => {
    if (!plan || !usage) return 0;
    const limit = plan.limits?.[limitName];
    if (!limit) return 0;
    const usageMap = {
      max_events_per_month: usage.events?.current,
      max_processes: usage.processes?.current,
      max_portal_users: usage.portal_users?.current,
    };
    const current = usageMap[limitName] || 0;
    return Math.round((current / limit) * 1000) / 10;
  }, [plan, usage]);

  return (
    <PlanContext.Provider value={{
      plan, usage, loading, error,
      hasFeature, getLimit, isOverLimit, usagePercent,
      refreshPlan: fetchPlan,
    }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const context = useContext(PlanContext);
  if (!context) throw new Error('usePlan must be used within PlanProvider');
  return context;
}
